<template>
  <div 
    class="button-widget" 
    :class="widgetClasses"
    :style="{ ...widgetStyles, textAlign: widget.alignment || 'left' }"
  >
    <a 
      v-if="widget.url"
      :href="widget.url"
      :target="widget.target || '_self'"
      class="btn btn-animated"
      :class="buttonClasses"
      :style="buttonStyles"
    >
      <i v-if="widget.icon && widget.iconPosition !== 'right'" :class="widget.icon" class="me-2"></i>
      <span>{{ widget.text }}</span>
      <i v-if="widget.icon && widget.iconPosition === 'right'" :class="widget.icon" class="ms-2"></i>
    </a>
    <button
      v-else
      type="button" 
      class="btn btn-animated"
      :class="buttonClasses"
      :style="buttonStyles"
      disabled
    >
      <i v-if="widget.icon && widget.iconPosition !== 'right'" :class="widget.icon" class="me-2"></i>
      <span>{{ widget.text }}</span>
      <i v-if="widget.icon && widget.iconPosition === 'right'" :class="widget.icon" class="ms-2"></i>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { ButtonWidget } from '@/types/pagebuilder'
import { useWidgetStyles } from '@/composables/useWidgetStyles'

interface Props {
  widget: ButtonWidget
}

const props = defineProps<Props>()

// Use common widget styles
const { widgetStyles, widgetClasses } = useWidgetStyles(props.widget)

const buttonClasses = computed(() => {
  const classes = []
  const style = props.widget.buttonStyle || 'primary'
  const usesCustomColors =
    style === 'custom' ||
    !!(
      props.widget.customBackgroundColor ||
      props.widget.customTextColor ||
      props.widget.customBorderColor
    )

  if (!usesCustomColors) {
    classes.push(`btn-${style}`)
  }
  
  // Size
  const size = props.widget.size || 'md'
  if (size === 'sm') classes.push('btn-sm')
  if (size === 'lg') classes.push('btn-lg')
  
  return classes.join(' ')
})

const buttonStyles = computed(() => {
  const styles: Record<string, string> = {
    width: props.widget.fullWidth ? '100%' : 'auto',
    margin: '0.5rem 0',
  }
  
  if (props.widget.customBackgroundColor) {
    styles.backgroundColor = `${props.widget.customBackgroundColor} !important`
  }
  if (props.widget.customTextColor) {
    styles.color = `${props.widget.customTextColor} !important`
  }
  if (props.widget.customBorderColor) {
    styles.borderColor = `${props.widget.customBorderColor} !important`
    styles.border = `2px solid ${props.widget.customBorderColor} !important`
  } else if (props.widget.customBackgroundColor) {
    styles.border = `2px solid ${props.widget.customBackgroundColor} !important`
  }
  
  return styles
})
</script>

<style scoped>
.button-widget {
  margin: 1rem 0;
}

.btn {
  text-decoration: none;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-animated {
  transform-origin: center;
}

.btn-animated:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
}

.btn-animated:active {
  transform: translateY(0);
}

.btn::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255,255,255,0.3);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.btn:hover::before {
  width: 300px;
  height: 300px;
}

.btn i {
  font-size: 1.1em;
  transition: transform 0.3s ease;
}

.btn:hover i {
  transform: scale(1.1);
}

/* Custom button style support */
.btn:not([class*="btn-"]) {
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  border-radius: 0.25rem;
  font-weight: 400;
}

.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  filter: brightness(0.95);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}
</style>